import axios from "axios";

/**
 * Changes the signed-in person's own password.
 *
 * SEPARATE FROM THE DIALOG ON PURPOSE. What the request carries and what a failure MEANS are the
 * two things a client can get wrong on its own, and both are testable without rendering anything.
 * The dialog is then only arrangement.
 *
 * THE SERVER IS THE CHECK. Every rule the dialog applies before calling this is applied again by
 * the endpoint, which also verifies the current password against the same predicate that lets
 * anybody in, and takes the account from the access token rather than from anything sent here.
 */
export type ChangePasswordResult =
    | {changed: true}
    | {changed: false; reason: "wrong-current" | "rejected" | "fault" | "unreachable"; code?: string | null};

export async function changePassword(
    tierPath: string,
    accessToken: string | null,
    currentPassword: string,
    newPassword: string,
): Promise<ChangePasswordResult> {
    const response: any = await axios({
        method: "POST",
        baseURL: process.env.REACT_APP_API_URL,
        url: `${tierPath}/v1/change_password`,
        headers: {"X-Cres-AccessToken": accessToken},
        // EXACTLY THESE TWO. No email: the endpoint reads the account from the token precisely so
        // that nothing in a request can redirect the change to somebody else.
        data: {CurrentPassword: currentPassword, NewPassword: newPassword},
    }).catch((e: any) => e.response || e);

    if (response instanceof Error || typeof response.status !== "number") {
        return {changed: false, reason: "unreachable"};
    }
    if (response.status === 204) {
        return {changed: true};
    }
    if (response.status === 401) {
        // The only failure the user can fix by retyping one field.
        return {changed: false, reason: "wrong-current"};
    }
    if (response.status >= 500) {
        return {changed: false, reason: "fault", code: response.data?.Code ?? null};
    }
    return {changed: false, reason: "rejected", code: response.data?.Code ?? null};
}

/** What to tell the user. Kept beside the mapping so a new reason cannot go unworded. */
export function messageFor(result: ChangePasswordResult): string | null {
    if (result.changed) return null;
    switch (result.reason) {
        case "wrong-current":
            return "That is not your current password.";
        case "unreachable":
            return "We could not reach the server. Please try again.";
        case "fault":
            // A 5xx is OURS. Saying the password was not accepted would send them to change a
            // password that is perfectly fine.
            return result.code
                ? `Something went wrong on our side. Reference ${result.code}.`
                : "Something went wrong on our side.";
        default:
            return "That password was not accepted. Please choose a different one.";
    }
}
